import { G } from '@mobily/ts-belt';
import { addYears, isAfter, startOfToday } from 'date-fns';
import { match } from 'ts-pattern';
import { z } from 'zod';

import { EMPLOYEE_MIN_LEGAL_WORK_AGE } from '@Common/constants/b2b/employees';
import {
  EmployeeGender,
  PostCreateB2bEmployeeRequest,
  TaxResidenceCode,
  ZodEmployeeGender,
  ZodPostCreateB2bEmployeeRequestUnrefined,
  ZodTaxResidenceCode,
} from '@Common/types/bo/b2b/employees';
import {
  EmployeeLeavingReason,
  EmployeeStatus,
  ZodEmployeeStatus,
  ZodLeavingReason,
} from '@Common/types/bo/b2b/employees_properties';
import { ZodId } from '@Common/types/request';
import { ZodCountryCode } from '@Common/types/zod/zod-country-code';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { ZodNIR13 } from '@Common/types/zod/zod-nir';
import { ZodMobilePhoneNumber } from '@Common/types/zod/zod-phone-number';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';
import { validateNIRComplete } from '@Common/validators/nirValidation';

export type EmployeeStatusExcel = 'Présent' | 'Present' | 'Sorti' | 'Retraité' | 'Retraite';
export type EmployeeTaxResidenceExcel = 'RS' | 'RNS' | 'NR';
export type EmployeeGenderExcel = 'Monsieur' | 'Madame';
export type EmployeeLeavingReasonExcel =
  | 'Fin de contrat'
  | 'Muté'
  | 'Mute'
  | 'Retraite'
  | 'Autre'
  | '';

/**
 * The "Pays" column is mandatory in the Excel template, so it cannot be null in a bulk row,
 * unlike the employee record it feeds (users_session.country_of_tax_residence).
 *
 * An empty cell is reported with the shared `required` key, so the BO shows its
 * "missing required data" message rather than a format error.
 */
const ZodMandatoryCountryCode = z.enum(ZodCountryCode.options, {
  error: (issue) =>
    G.isNullable(issue.input) ? 'country must not be empty (required)' : 'Invalid country code',
});

export const ZodB2bEmployeeBulkCreateRow = z
  .object({
    ...ZodPostCreateB2bEmployeeRequestUnrefined.pick({
      registration_number: true,
      ntt: true,
      firstname: true,
      lastname: true,
      name_in_use: true,
      email: true,
      street: true,
      postalcode: true,
      city: true,
      language: true,
      nationality: true,
      social_status: true,
      phone_number: true,
      role_in_company: true,
      work_starting_date: true,
      work_ending_date: true,
      taiga_policy_holder_id: true,
      date_of_birth: true,
      country_of_birth: true,
      postcode_of_birth: true,
      place_of_birth: true,
      country: true,
    }).shape,
    contract_ids: z.preprocess(
      (val: unknown): number[] | false => {
        if (typeof val === 'string' && val.trim()) {
          const parsed = val.split(',').map((id) => parseInt(id.trim(), 10));

          if (parsed.some((id) => isNaN(id))) {
            return false;
          }

          return parsed;
        }
        if (Array.isArray(val) && val.every((item): item is number => typeof item === 'number')) {
          return val;
        }
        return [];
      },
      z.array(z.number()).min(1, 'At least one contract ID is required (one_required)'),
    ),
    status: z.preprocess((val: unknown) => {
      if (typeof val === 'string') {
        return match<EmployeeStatusExcel, EmployeeStatus>(val as EmployeeStatusExcel)
          .with('Présent', 'Present', () => 'current')
          .with('Sorti', () => 'past')
          .with('Retraité', 'Retraite', () => 'retired')
          .otherwise(() => val as EmployeeStatus);
      }
      return val;
    }, ZodEmployeeStatus),
    leaving_reason: z.preprocess((val: unknown) => {
      if (typeof val === 'string') {
        return match<EmployeeLeavingReasonExcel, EmployeeLeavingReason | null>(
          val as EmployeeLeavingReasonExcel,
        )
          .with('Retraite', () => 'retirement')
          .with('Fin de contrat', () => 'work_ended')
          .with('Muté', 'Mute', () => 'job_transfer')
          .with('Autre', () => 'other')
          .with('', () => null)
          .otherwise(() => val as EmployeeLeavingReason);
      }
      return val;
    }, ZodLeavingReason.nullable()),
    tax_residence_code: z.preprocess((val: unknown) => {
      if (typeof val === 'string') {
        return match<EmployeeTaxResidenceExcel, TaxResidenceCode>(val as EmployeeTaxResidenceExcel)
          .with('RS', () => 'tax_resident')
          .with('RNS', () => 'resident_exempt')
          .with('NR', () => 'non_tax_resident')
          .otherwise(() => val as TaxResidenceCode);
      }
      return val;
    }, ZodTaxResidenceCode),
    gender: z.preprocess((val: unknown) => {
      if (typeof val === 'string') {
        return match<EmployeeGenderExcel, EmployeeGender>(val as EmployeeGenderExcel)
          .with('Monsieur', () => 'male')
          .with('Madame', () => 'female')
          .otherwise(() => val as EmployeeGender);
      }
      return val;
    }, ZodEmployeeGender),
    phone_number: z.preprocess(
      (val) => (typeof val === 'string' && !val.startsWith('+') ? `+${val}` : val),
      ZodMobilePhoneNumber.nullable(),
    ),
    country: ZodMandatoryCountryCode,
    csp_code: z.preprocess((val) => {
      if (G.isNullable(val) || (typeof val === 'string' && val.length === 0)) {
        return null;
      }

      if (typeof val === 'string') {
        const code = val.replace(/ /g, '');
        if (code.length === 0) {
          return null;
        }
        return code;
      }

      return val;
    }, ZodTrimmedString.nullish()),
    is_npai: z.stringbool().nullish(),
    is_npai_mailing: z.stringbool().nullish(),
    id_external: ZodId.nullish(),
    nir: ZodNIR13.nullable(),
  })
  .refine((data) => data.nir !== null || data.ntt !== null, {
    message: 'NIR or NTT must not be null (not_null)',
    path: ['nir'],
  })
  // Work ending date checks
  .refine(
    (data) =>
      !data.work_ending_date ||
      !data.work_starting_date ||
      isAfter(data.work_ending_date, data.work_starting_date),
    {
      message: 'must be later than work_starting_date (later_work_starting_date)',
      path: ['work_ending_date'],
    },
  )
  .refine((data) => data.status === 'current' || data.work_ending_date, {
    message:
      'work_ending_date should not be empty when status is different than "Présent" (not_empty_status_old)',
    path: ['work_ending_date'],
  })
  .refine((data) => data.work_ending_date || !data.leaving_reason, {
    message:
      'work_ending_date should not be empty when leaving_reason is set (not_empty_leaving_reason_set)',
    path: ['work_ending_date'],
  })
  // Leaving reason checks
  .refine((data) => data.status === 'current' || data.leaving_reason, {
    message:
      'leaving_reason should not be empty when status is different than "Présent" (not_empty_status_old)',
    path: ['leaving_reason'],
  })
  .refine((data) => data.leaving_reason || !data.work_ending_date, {
    message:
      'leaving_reason should not be empty when work_ending_date is set (not_empty_work_ending_date_set)',
    path: ['leaving_reason'],
  })
  .refine(
    (data) =>
      !data.leaving_reason || data.status !== 'retired' || data.leaving_reason === 'retirement',
    {
      message:
        'leaving_reason should be set to "Retraite" when status is "Retraité" (retirement_status_retired)',
      path: ['leaving_reason'],
    },
  )
  // Status checks
  .refine(
    (data) =>
      data.status === 'current' || !data.work_ending_date || data.work_ending_date < startOfToday(),
    {
      message:
        'status should be set to "Présent" when work_ending_date is set in future (current_work_ending_date_future)',
      path: ['status'],
    },
  )
  .refine(
    (data) =>
      data.status !== 'current' ||
      !data.work_ending_date ||
      data.work_ending_date >= startOfToday(),
    {
      message:
        'status should be set to "Sorti" or "Retraité" when work_ending_date is set in past (old_work_ending_date_past)',
      path: ['status'],
    },
  )
  .refine(
    (data) =>
      data.leaving_reason !== 'retirement' ||
      data.status === 'retired' ||
      !data.work_ending_date ||
      data.work_ending_date >= startOfToday(),
    {
      message:
        'status should be set to "Retraité" when leaving_reason is "Retraite" (retired_leaving_reason_retirement)',
      path: ['status'],
    },
  )
  .refine(
    (data) => {
      if (G.isNotNullable(data.date_of_birth) && G.isNotNullable(data.work_starting_date)) {
        const workLegalAge = addYears(data.date_of_birth, EMPLOYEE_MIN_LEGAL_WORK_AGE);
        return data.work_starting_date >= workLegalAge;
      }
      return true;
    },
    {
      message: 'Work starting date must be higher than zero (min_legal_age)',
      path: ['work_starting_date'],
    },
  )
  .superRefine((data, ctx) => {
    // NIR validation with context from other fields
    if (!data.nir) {
      return;
    }

    const nirValidationResult = validateNIRComplete({
      nir: data.nir,
      gender: data.gender,
      date_of_birth: data.date_of_birth,
      postcode_of_birth: data.postcode_of_birth,
    });

    if (!nirValidationResult.isValid) {
      ctx.addIssue({
        code: 'custom',
        path: ['nir'],
        message: nirValidationResult.errors
          .slice(0, 2) // Limit to 2 errors to avoid too many messages
          .map((errorCode) => `(${errorCode})`)
          .join(','),
      });
    }
  });

export type B2bEmployeeBulkCreateRow = z.infer<typeof ZodB2bEmployeeBulkCreateRow>;

export const ZodB2bEmployeeBulkUpdateRow = z.object({
  ...ZodB2bEmployeeBulkCreateRow.shape,
  id: ZodId,
  user_id: ZodId,
});

export type B2bEmployeeBulkUpdateRow = z.infer<typeof ZodB2bEmployeeBulkUpdateRow>;

export const EmployeeExcelColumnMapping = {
  nir: 'NIR',
  ntt: 'NTT',
  gender: 'Civilite',
  firstname: 'Prenom',
  lastname: 'Nom',
  name_in_use: 'Nom_usage',
  email: 'Email',
  street: 'Adresse',
  postalcode: 'Code_postal',
  city: 'Ville',
  country: 'Pays',
  language: 'Langue',
  status: 'Situation',
  date_of_birth: 'Date_naissance',
  country_of_birth: 'Pays_naissance',
  place_of_birth: 'Commune_naissance',
  postcode_of_birth: 'Code_naissance',
  nationality: 'Nationalite',
  work_starting_date: 'Date_entree',
  work_ending_date: 'Date_sortie',
  leaving_reason: 'Raison_depart',
  social_status: 'Statut_social',
  tax_residence_code: 'Code_resident_fiscal',
  phone_number: 'Num_mobile',
  role_in_company: 'Fonction',
  registration_number: 'Matricule',
  contract_ids: 'Contrats',
  csp_code: 'CSP_code',
} as const;

export type B2bEmployeeExcelColumns = Record<
  (typeof EmployeeExcelColumnMapping)[keyof typeof EmployeeExcelColumnMapping],
  string | null
>;

export type B2bEmployeeExcelFields = {
  [K in keyof typeof EmployeeExcelColumnMapping]: K extends 'gender' ? string | null : string;
};

const ZodPostB2bEmployeeBulkCreateRequest = z.object({
  company_id: ZodId,
  file_content: ZodTrimmedString,
  automatic_onboarding: z.boolean(),
});
export type PostB2bEmployeeBulkCreateRequest = z.TypeOf<typeof ZodPostB2bEmployeeBulkCreateRequest>;

const ZodPostB2bEmployeeBulkCreateResponse = z.object({
  id: ZodId,
  b2b_employees_bulk_upload_result_id: ZodId.nullable(),
});
export type PostB2bEmployeeBulkCreateResponse = z.TypeOf<
  typeof ZodPostB2bEmployeeBulkCreateResponse
>;

export const postB2bEmployeesBulkCreateEndpoint = endpoint({
  method: 'POST',
  body: ZodPostB2bEmployeeBulkCreateRequest,
  responseBody: ZodPostB2bEmployeeBulkCreateResponse,
});

export type PostB2bEmployeesBulkCreateEndpoint = GetEndpointType<
  typeof postB2bEmployeesBulkCreateEndpoint
>;

export const ZodBulkCreateEmployeeErrors = z.record(
  ZodTrimmedString.regex(/^\d+$/),
  z.record(ZodTrimmedString, ZodTrimmedString),
);
export type BulkCreateEmployeeErrors = z.TypeOf<typeof ZodBulkCreateEmployeeErrors>;

export const ZodBulkCreateEmployeeRawData = z.array(z.record(ZodTrimmedString, ZodTrimmedString));
export type BulkCreateEmployeeRawData = z.TypeOf<typeof ZodBulkCreateEmployeeRawData>;

export const ZodBulkCreateEmployeeOverrides = z.record(
  ZodTrimmedString.regex(/^\d+$/),
  z
    .record(ZodTrimmedString, z.union([ZodTrimmedString.nullable(), z.number()]))
    .and(z.object({ id: ZodId, user_id: ZodId })),
);
export type BulkCreateEmployeeOverrides = z.TypeOf<typeof ZodBulkCreateEmployeeOverrides>;

export const ZodBulkCreateValidateResponseBody = z.union([
  z.object({
    type: z.literal('success'),
    id: ZodId,
    raw_data: ZodBulkCreateEmployeeRawData,
    ignored_overrides: ZodBulkCreateEmployeeOverrides.optional(),
  }),
  z.object({
    type: z.literal('validation_error'),
    id: ZodId,
    raw_data: ZodBulkCreateEmployeeRawData,
    errors: ZodBulkCreateEmployeeErrors,
  }),
  z.object({
    type: z.literal('overwrite_confirmation'),
    id: ZodId,
    overrides: ZodBulkCreateEmployeeOverrides,
    ignored_overrides: ZodBulkCreateEmployeeOverrides,
    raw_data: ZodBulkCreateEmployeeRawData,
  }),
]);

export const postEmployeesBulkCreateValidateEndpoint = endpoint({
  method: 'POST',
  body: z.object({
    company_id: ZodId,
    file_content: ZodTrimmedString,
    automatic_onboarding: z.boolean().optional(),
  }),
  responseBody: ZodBulkCreateValidateResponseBody,
});

export type PostEmployeesBulkCreateValidateEndpoint = GetEndpointType<
  typeof postEmployeesBulkCreateValidateEndpoint
>;

// The bulk upload file always provides a country (mandatory "Pays" column), but the
// single-employee creation flow reuses this payload and still allows it to be empty.
export type InsertB2bEmployeePayload = Omit<B2bEmployeeBulkCreateRow, 'country'> &
  Partial<PostCreateB2bEmployeeRequest> & {
    country: PostCreateB2bEmployeeRequest['country'];
  };
